import { useEffect, useState } from "react";
import { Clock, MapPin, Navigation, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

export default function RoutesBoard() {
  const [day, setDay] = useState(new Date().toISOString().split("T")[0]);
  const [data, setData] = useState(null);

  const load = async (d) => {
    try {
      const { data } = await api.get("/routes", { params: { day: d } });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(day); }, []);

  const directionsUrl = (s) => s.lat != null && s.lng != null
    ? `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(s.address || s.venue)}`;

  return (
    <div data-testid="routes-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Routes</h1>
          <p className="mt-1 text-sm text-muted-foreground">The day's delivery stops and timed buffet collections.</p>
        </div>
        <Input data-testid="routes-date-input" type="date" value={day} onChange={(e) => { setDay(e.target.value); load(e.target.value); }} className="w-44" />
      </div>

      {data && (
        <div className="space-y-6">
          <div className="space-y-3" data-testid="routes-stops">
            {data.stops.length === 0 && <p className="border border-border bg-white px-5 py-10 text-center text-sm text-muted-foreground">No deliveries this day.</p>}
            {data.stops.map((s, i) => (
              <div key={i} className="flex items-center justify-between border border-border bg-white p-5" data-testid={`route-stop-${i}`}>
                <div>
                  <p className="font-heading text-base font-bold">{s.venue || "Venue TBC"}</p>
                  <p className="mt-0.5 text-sm text-muted-foreground">{s.ref} · {s.customer} · {s.guests} guests{(s.units || []).length > 0 ? ` · ${s.units.join(", ")}` : ""}</p>
                  {s.address && <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {s.address}</p>}
                </div>
                <a href={directionsUrl(s)} target="_blank" rel="noreferrer" data-testid={`route-directions-${i}`}
                   className="flex items-center gap-2 border border-border px-4 py-2 text-sm font-semibold text-ink transition-colors duration-150 hover:bg-muted">
                  <Navigation className="h-4 w-4" /> Directions
                </a>
              </div>
            ))}
          </div>

          <div className="border border-border bg-white p-6" data-testid="routes-pickups">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground"><ShoppingBag className="h-4 w-4" /> Buffet collections ({data.pickups.length})</p>
            {data.pickups.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">No collections this day.</p> : (
              <div className="mt-3 space-y-2">
                {data.pickups.map((p, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-border pb-2 text-sm last:border-0" data-testid={`route-pickup-${i}`}>
                    <span className="flex items-center gap-2 font-semibold"><Clock className="h-4 w-4 text-brand" /> {p.time || "—"} — {p.customer}</span>
                    <span className="text-xs text-muted-foreground">{p.items} items · {p.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
